/**
 * Row actions for a pending sign-up request: convert it into a client, or
 * reject it.
 *
 * Converting does NOT create the client here. It opens the manual client
 * creation that already exists, pre-filled with what the visitor gave us, and
 * the request is marked converted only once that creation succeeds (see
 * ClientCreate). Abandoning the form therefore leaves the request pending,
 * which is the honest outcome: nothing was created.
 *
 * A decided request shows no actions — the decision is final.
 */

import { MouseEvent, useState } from 'react';
import {
  Confirm,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PersonAddAlt1Outlined as ConvertIcon,
  BlockOutlined as RejectIcon,
} from '@mui/icons-material';
import { decisionErrorKey, isAlreadyDecided } from './decision';

interface SignupRequestActionsProps {
  /** Column header, read by Datagrid from the element props */
  label?: string;
  /** Read by Datagrid from the element props */
  sortable?: boolean;
}

export const SignupRequestActions = (_props: SignupRequestActionsProps = {}) => {
  const record = useRecordContext();
  const translate = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  if (!record || record.state !== 'pending') return null;

  const stop = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleConvert = (event: MouseEvent) => {
    stop(event);
    setAnchorEl(null);
    // Carried in the navigation state rather than the address, so the
    // visitor's personal data does not end up in the browser history.
    navigate('/clients/create', {
      state: {
        record: {
          full_name: record.full_name ?? '',
          email: record.email ?? '',
          company_name: record.company_name ?? '',
          contact_no: record.contact_no ?? '',
          plan_id: record.plan_id ?? undefined,
        },
        signupRequestId: record.signup_request_id,
      },
    });
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await dataProvider.update('signupRequests', {
        id: record.id,
        data: { state: 'rejected' },
        previousData: record,
      });
      notify('resources.signupRequests.notifications.rejected', { type: 'info' });
    } catch (error) {
      notify(translate(decisionErrorKey(error)), {
        type: isAlreadyDecided(error) ? 'warning' : 'error',
      });
    } finally {
      setRejecting(false);
      setConfirmOpen(false);
      refresh();
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          stop(event);
          setAnchorEl(event.currentTarget);
        }}
        aria-label={translate('ra.action.open_menu', { _: 'Acciones' })}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(event) => {
          stop(event as MouseEvent);
          setAnchorEl(null);
        }}
      >
        <MenuItem onClick={handleConvert}>
          <ListItemIcon>
            <ConvertIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.signupRequests.actions.convert')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            stop(event);
            setAnchorEl(null);
            setConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <RejectIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.signupRequests.actions.reject')}</ListItemText>
        </MenuItem>
      </Menu>
      <Confirm
        isOpen={confirmOpen}
        loading={rejecting}
        title="resources.signupRequests.confirm.reject_title"
        content="resources.signupRequests.confirm.reject_content"
        onConfirm={handleReject}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default SignupRequestActions;
